<template>
  <div class="position-relative cover-wrapper">
    <!-- Cover Photo -->
    <img
      class="cover-img"
      :src="coverPreview"
      alt="Cover Photo"
    />

    <!-- Edit Button -->
    <div v-if="isEditable" class="position-absolute top-0 end-0">
      <input
        type="file"
        ref="coverInput"
        class="d-none"
        accept="image/*"
        @change="onCoverSelect"
      />
      <button
        v-if="removable && coverPreview !== defaultCoverPhoto"
        type="button"
        class="btn btn-icon btn-sm btn-danger rounded-0"
        title="Remove Photo"
        @click="onRemove"
      >
        <i class="fas fa-times"></i>
      </button>
      <button
        type="button"
        class="btn btn-icon btn-sm btn-primary rounded-0"
        title="Edit Cover Photo"
        @click="coverInput.click()"
      >
        <i class="fas fa-pencil"></i>
      </button>
    </div>

    <!-- Cropper -->
    <Cropper
      :img="cropper.img"
      :type="cropper.type"
      :name="cropper.name"
      :show="cropper.show"
      :isAspectRatio="false"
      :aspectRatio="3.6"
      @onImageCropped="onImageCropped"
      @closeModal="closeCropper"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import Cropper from '@/components/molecule/contact/ImageCropper'

/* ---------------- props & emits ---------------- */
const props = defineProps({
  coverPhoto: {
    type: File,
    default: '',
  },
  isEditable: {
    type: Boolean,
    default: false,
  },
  removable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['uploaded', 'removed'])

/* ---------------- refs ---------------- */
const coverInput = ref(null)
const previewCover = ref('')
const unProcessedImage = ref(null)

/* ---------------- constants ---------------- */
const defaultCoverPhoto = '/app-assets/images/cover-photo.png'

/* ---------------- cropper state ---------------- */
const cropper = ref({
  show: false,
  img: '',
  type: '',
  name: '',
})

/* ---------------- computed ---------------- */
const coverPreview = computed(() => {
  if (previewCover.value) return previewCover.value
  if (props.coverPhoto) return props.coverPhoto
  return defaultCoverPhoto
})

/* ---------------- methods ---------------- */
const onRemove = () => {
  previewCover.value = defaultCoverPhoto
  emit('removed')
}

const onCoverSelect = () => {
  unProcessedImage.value = coverInput.value.files[0]
  if (!unProcessedImage.value) return

  const reader = new FileReader()
  reader.readAsDataURL(unProcessedImage.value)

  reader.onload = e => {
    cropper.value = {
      show: true,
      img: e.target.result,
      type: unProcessedImage.value.type,
      name: unProcessedImage.value.name,
    }
  }
}

const onImageCropped = async image => {
  previewCover.value = image
  cropper.value.show = false

  const file = await dataUrlToFile(image, unProcessedImage.value.name)
  emit('uploaded', file)
  resetCoverInput();
}

const closeCropper = () => {
  cropper.value.show = false
  resetCoverInput();
}

const resetCoverInput = () => {
  if (coverInput.value) {
    coverInput.value.value = null
  }
}

const dataUrlToFile = async (dataUrl, fileName) => {
  const blob = await (await fetch(dataUrl)).blob()
  return new File([blob], fileName, { type: blob.type })
}
</script>

<style scoped>
.cover-wrapper {
  position: relative;
}

.cover-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
